/** Image attach helpers: gallery, lightbox, composer (file / camera / screenshot). */

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp'
const MAX = 8

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function renderAttachmentGallery(attachments) {
  const list = Array.isArray(attachments) ? attachments : []
  if (!list.length) return ''
  const items = list.map((a) => {
    const url = a.url || ''
    const name = escapeHtml(a.filename || 'imagen')
    if (a.is_image !== false && url) {
      return `<a class="ta-gallery__item" href="${escapeHtml(url)}" data-ta-lightbox="${escapeHtml(url)}" title="${name}">
        <img src="${escapeHtml(url)}" alt="${name}" loading="lazy" />
      </a>`
    }
    return `<a class="ta-gallery__item" href="${escapeHtml(url)}" title="${name}" style="display:flex;align-items:center;justify-content:center;font-size:11px;padding:4px;text-align:center;color:inherit;text-decoration:none;">${escapeHtml((a.filename || 'archivo').slice(0, 18))}</a>`
  }).join('')
  return `<div class="ta-gallery">${items}</div>`
}

export function ensureLightbox() {
  let box = document.getElementById('ta-lightbox')
  if (box) return box
  box = document.createElement('div')
  box.id = 'ta-lightbox'
  box.className = 'ta-lightbox'
  box.hidden = true
  box.setAttribute('role', 'dialog')
  box.innerHTML = `<button type="button" class="ta-lightbox__close" aria-label="Cerrar">×</button><img class="ta-lightbox__img" alt="" />`
  document.body.appendChild(box)
  const img = box.querySelector('.ta-lightbox__img')
  const hide = () => {
    box.hidden = true
    img.removeAttribute('src')
    document.body.classList.remove('ta-lightbox-open')
  }
  box.querySelector('.ta-lightbox__close').addEventListener('click', hide)
  box.addEventListener('click', (ev) => { if (ev.target === box) hide() })
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !box.hidden) hide()
  })
  box._show = (src, alt) => {
    img.src = src
    img.alt = alt || 'Adjunto'
    box.hidden = false
    document.body.classList.add('ta-lightbox-open')
  }
  return box
}

export function bindLightbox(root = document) {
  ensureLightbox()
  root.addEventListener('click', (ev) => {
    const thumb = ev.target.closest('[data-ta-lightbox]')
    if (!thumb) return
    ev.preventDefault()
    ensureLightbox()._show(thumb.getAttribute('data-ta-lightbox') || thumb.src, thumb.getAttribute('alt') || '')
  })
}

/**
 * Mount composer into a form. Returns { getFiles, destroy }.
 * Files are kept in memory and merged into FormData on submit by caller,
 * or written into a hidden file input named images[].
 */
export function mountComposer(form, { maxFiles = MAX } = {}) {
  if (!form || form.dataset.taReady) return null
  form.dataset.taReady = '1'
  form.setAttribute('enctype', 'multipart/form-data')

  const files = []
  const previewUrls = []
  const wrap = document.createElement('div')
  wrap.className = 'ta-composer'
  wrap.innerHTML = `
    <div class="ta-composer__bar">
      <button type="button" class="ta-btn" data-act="file">Adjuntar imagen</button>
      <button type="button" class="ta-btn" data-act="cam">Cámara</button>
      <button type="button" class="ta-btn" data-act="shot">Captura pantalla</button>
    </div>
    <div class="ta-composer__preview" aria-live="polite"></div>
    <p class="ta-composer__hint">JPG, PNG, GIF o WebP · máx. ${maxFiles} imágenes</p>
    <input type="file" accept="${ACCEPT}" multiple hidden data-act="file-input" />
    <input type="file" accept="${ACCEPT}" capture="environment" hidden data-act="cam-input" />
  `
  const ta = form.querySelector('textarea')
  if (ta?.parentNode) ta.parentNode.insertBefore(wrap, ta.nextSibling)
  else form.prepend(wrap)

  const preview = wrap.querySelector('.ta-composer__preview')
  const fileInput = wrap.querySelector('[data-act="file-input"]')
  const camInput = wrap.querySelector('[data-act="cam-input"]')

  function sync() {
    previewUrls.forEach((u) => { try { URL.revokeObjectURL(u) } catch (_) {} })
    previewUrls.length = 0
    preview.innerHTML = ''
    const dt = new DataTransfer()
    files.forEach((f, idx) => {
      dt.items.add(f)
      const url = URL.createObjectURL(f)
      previewUrls.push(url)
      const item = document.createElement('div')
      item.className = 'ta-thumb'
      item.innerHTML = `<img src="${url}" alt="${escapeHtml(f.name)}" data-ta-lightbox="${url}" /><button type="button" class="ta-thumb__rm" aria-label="Quitar">×</button>`
      item.querySelector('.ta-thumb__rm').addEventListener('click', () => {
        files.splice(idx, 1)
        sync()
      })
      preview.appendChild(item)
    })
    // Keep a real file input for multipart browsers that serialize FormData from form
    let named = form.querySelector('input[name="images[]"][data-ta-named]')
    if (!named) {
      named = document.createElement('input')
      named.type = 'file'
      named.name = 'images[]'
      named.multiple = true
      named.hidden = true
      named.dataset.taNamed = '1'
      form.appendChild(named)
    }
    named.files = dt.files
  }

  function addList(list) {
    Array.from(list || []).forEach((f) => {
      if (!f || !/^image\//.test(f.type)) return
      if (files.length >= maxFiles) return
      files.push(f)
    })
    sync()
  }

  wrap.querySelector('[data-act="file"]').addEventListener('click', () => fileInput.click())
  fileInput.addEventListener('change', () => { addList(fileInput.files); fileInput.value = '' })

  wrap.querySelector('[data-act="cam"]').addEventListener('click', async () => {
    if (navigator.mediaDevices?.getUserMedia) {
      const blob = await captureCamera()
      if (blob) addList([new File([blob], `camara-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' })])
    } else {
      camInput.click()
    }
  })
  camInput.addEventListener('change', () => { addList(camInput.files); camInput.value = '' })

  wrap.querySelector('[data-act="shot"]').addEventListener('click', async () => {
    const blob = await captureScreen()
    if (blob) addList([new File([blob], `captura-${Date.now()}.png`, { type: 'image/png' })])
  })

  return {
    getFiles: () => files.slice(),
    appendToFormData(fd) {
      files.forEach((f) => fd.append('images[]', f, f.name))
      return fd
    },
  }
}

async function captureCamera() {
  let stream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    })
  } catch {
    alert('No se pudo acceder a la cámara.')
    return null
  }
  return new Promise((resolve) => {
    const modal = document.createElement('div')
    modal.className = 'ta-cam-modal'
    modal.innerHTML = `<video class="ta-cam-modal__video" autoplay playsinline></video>
      <div class="ta-cam-modal__actions">
        <button type="button" class="ta-btn ta-btn--primary" data-act="snap">Tomar foto</button>
        <button type="button" class="ta-btn" data-act="cancel">Cancelar</button>
      </div>`
    document.body.appendChild(modal)
    const video = modal.querySelector('video')
    video.srcObject = stream
    const cleanup = () => {
      stream.getTracks().forEach((t) => t.stop())
      modal.remove()
    }
    modal.querySelector('[data-act="cancel"]').onclick = () => { cleanup(); resolve(null) }
    modal.querySelector('[data-act="snap"]').onclick = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || 1280
      canvas.height = video.videoHeight || 720
      canvas.getContext('2d').drawImage(video, 0, 0)
      canvas.toBlob((blob) => { cleanup(); resolve(blob) }, 'image/jpeg', 0.92)
    }
  })
}

async function captureScreen() {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    alert('Tu navegador no permite capturar la pantalla.')
    return null
  }
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
    const track = stream.getVideoTracks()[0]
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    video.playsInline = true
    await video.play()
    await new Promise((r) => setTimeout(r, 250))
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    canvas.getContext('2d').drawImage(video, 0, 0)
    track.stop()
    stream.getTracks().forEach((t) => t.stop())
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
  } catch {
    return null
  }
}
